import React, { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useLanguage } from '../lib/language'
import { supabase } from '../lib/supabase'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'

// ── SERVICE CATEGORIES ───────────────────────────────────────
const SERVICE_CATEGORIES = [
  {
    id: 'affidavits', icon: '📋',
    name_en: 'Affidavits & Sworn Statements',
    name_es: 'Declaraciones Juradas',
    desc_en: 'Need to put something in writing and make it official? We witness it, certify it, and make it legal — simple and stress-free.',
    desc_es: 'Documentos donde juras bajo juramento que algo es verdad',
    services_en: ['Affidavit (General)','Acknowledgement','Jurat / Oath','Name Change Affidavit','Residency Affidavit','Identity Affidavit','Lost Documents Affidavit','Financial Affidavit','Heirship Affidavit','Estate Affidavit','Certified Identity Statement','Certified Residency Statement'],
    services_es: ['Declaración Jurada (General)','Reconocimiento','Juramento','Declaración de Cambio de Nombre','Declaración de Residencia','Declaración de Identidad','Declaración de Documentos Perdidos','Declaración Financiera','Declaración de Heredero','Declaración de Patrimonio','Declaración Certificada de Identidad','Declaración Certificada de Residencia'],
    plains_en: ['A sworn written statement about any topic','Confirming you signed of your own free will','Swearing under oath that a document is true','Confirming your legal name has changed','Proving where you live','Confirming who you are','Swearing that an original document was lost','A sworn statement about your finances','Proving you are an heir to an estate','Sworn statement related to an estate','Official statement confirming your identity','Official statement confirming your address'],
    plains_es: ['Una declaración escrita jurada','Confirmar que firmaste por voluntad propia','Jurar que un documento es verdadero','Confirmar que tu nombre legal ha cambiado','Probar dónde vives','Confirmar quién eres','Jurar que un documento se perdió','Una declaración jurada sobre tus finanzas','Probar que eres heredero','Declaración jurada de patrimonio','Declaración oficial de identidad','Declaración oficial de dirección'],
  },
  {
    id: 'family', icon: '👨‍👩‍👧',
    name_en: 'Family & Personal Documents',
    name_es: 'Documentos Familiares y Personales',
    desc_en: 'From permission slips to adoption papers — whatever your family needs notarized, we come to you with care and patience.',
    desc_es: 'Documentos para asuntos familiares y personales',
    services_en: ['Parental Consent Form','Tattoo Parental Consent (Minor)','Travel Consent Letter for Minors','Adoption Documents','Guardianship / Custodian Affidavit','School Documents','Proof of Life / Life Certificate'],
    services_es: ['Formulario de Consentimiento Parental','Consentimiento para Tatuaje (Menor)','Carta de Consentimiento de Viaje','Documentos de Adopción','Declaración de Tutela / Custodia','Documentos Escolares','Certificado de Vida'],
    plains_en: ['Permission from a parent for a child\'s activity','Parent\'s written permission for a minor to get a tattoo','Permission for a child to travel without both parents','Legal paperwork related to adopting a child','Documents related to who takes care of a child','Any school-related paperwork needing notarization','Confirming someone is alive — often needed for pensions'],
    plains_es: ['Permiso de un padre para actividad de un menor','Permiso escrito del padre para que un menor se tatúe','Permiso para que un menor viaje sin ambos padres','Papeleo legal relacionado con la adopción','Documentos relacionados con tutela','Cualquier papeleo escolar que necesite notarización','Confirmar que alguien está vivo'],
  },
  {
    id: 'legal', icon: '⚖️',
    name_en: 'Legal & Court Documents',
    name_es: 'Documentos Legales y Judiciales',
    desc_en: 'Powers of attorney, living wills, court paperwork — we handle the important stuff so you don\'t have to figure it out alone.',
    desc_es: 'Documentos relacionados con asuntos legales y judiciales',
    services_en: ['Power of Attorney','Advance Health Directive / Living Will','Living Will','Court Documents','Hold Harmless Agreement','Consent & Release Form','Private Party Agreement'],
    services_es: ['Poder Notarial','Directiva Anticipada de Salud','Testamento Vital','Documentos Judiciales','Acuerdo de Indemnización','Formulario de Consentimiento','Acuerdo entre Particulares'],
    plains_en: ['Giving someone legal authority to act on your behalf','Your wishes for medical care if you can\'t speak for yourself','Written instructions for end-of-life medical care','Any paperwork filed with or from a court','Agreement releasing someone from responsibility','Giving permission and releasing liability','A contract between two private individuals'],
    plains_es: ['Dar autoridad legal a alguien para actuar en tu nombre','Tus deseos de atención médica','Instrucciones para atención médica al final de la vida','Cualquier papeleo de un tribunal','Acuerdo que libera responsabilidad','Dar permiso y liberar responsabilidad','Un contrato entre dos personas privadas'],
  },
  {
    id: 'medical', icon: '🏥',
    name_en: 'Medical & Healthcare',
    name_es: 'Médico y Salud',
    desc_en: 'Authorizing someone to make medical decisions or access your records? We make sure it\'s done right and recognized everywhere.',
    desc_es: 'Documentos relacionados con tu salud',
    services_en: ['Medical Authorization Form','Insurance Claims Form'],
    services_es: ['Formulario de Autorización Médica','Formulario de Reclamo de Seguro'],
    plains_en: ['Allowing someone to make medical decisions or access your records','Paperwork for filing an insurance claim needing notarization'],
    plains_es: ['Permitir que alguien tome decisiones médicas','Papeleo para un reclamo de seguro'],
  },
  {
    id: 'financial', icon: '🏦',
    name_en: 'Financial & Banking',
    name_es: 'Financiero y Bancario',
    desc_en: 'Bank forms, pension documents, promissory notes — we notarize your financial paperwork quickly and accurately.',
    desc_es: 'Documentos relacionados con finanzas y bancos',
    services_en: ['Banking Forms','Pension / Retirement Forms','Promissory Note (Non-Real Estate)','Bank Safe Deposit Box Inventory / Drilling'],
    services_es: ['Formularios Bancarios','Formularios de Pensión / Jubilación','Pagaré (No Inmobiliario)','Inventario de Caja de Seguridad'],
    plains_en: ['Bank paperwork that requires a notary\'s seal','Documents related to your retirement or pension','A written promise to repay a loan (not for a house)','Opening or documenting the contents of a safe deposit box'],
    plains_es: ['Papeleo bancario que requiere sello notarial','Documentos de jubilación o pensión','Una promesa escrita de pagar un préstamo','Documentar el contenido de una caja de seguridad'],
  },
  {
    id: 'realestate', icon: '🏠',
    name_en: 'Real Estate & Property',
    name_es: 'Bienes Raíces y Propiedad',
    desc_en: 'Buying, selling, or renting? We notarize your property documents on your schedule — wherever you need us.',
    desc_es: 'Documentos relacionados con propiedades y alquileres',
    services_en: ['Lease Agreement','Rental Verification Form','Bill of Sale'],
    services_es: ['Contrato de Arrendamiento','Formulario de Verificación de Alquiler','Factura de Venta'],
    plains_en: ['A rental contract between landlord and tenant','Confirming where you rent or your rental history','Proof of purchase when selling or buying something'],
    plains_es: ['Un contrato de alquiler entre propietario e inquilino','Confirmar historial de alquiler','Prueba de compra al vender o comprar algo'],
  },
  {
    id: 'government', icon: '🏛️',
    name_en: 'Employment & Government',
    name_es: 'Empleo y Gobierno',
    desc_en: 'I-9s, passport forms, DMV paperwork, employment verification — we handle official documents without the headache.',
    desc_es: 'Documentos para trabajo y agencias gubernamentales',
    services_en: ['Employment Verification Form','I-9 Form','DMV Forms','Passport Forms','VIN / Vehicle Identification Statement'],
    services_es: ['Formulario de Verificación de Empleo','Formulario I-9','Formularios del DMV','Formularios de Pasaporte','Declaración de VIN / Vehículo'],
    plains_en: ['Confirming your employment status or history','Work eligibility verification form required by employers','Any Department of Motor Vehicles paperwork needing notarization','Passport application or renewal documents','Official statement about a vehicle\'s identification number'],
    plains_es: ['Confirmar tu estado de empleo','Formulario de elegibilidad de trabajo','Papeleo del Departamento de Vehículos Motorizados','Documentos de pasaporte','Declaración del número de identificación de un vehículo'],
  },
  {
    id: 'dealership', icon: '🚗',
    name_en: 'Car Dealership Services',
    name_es: 'Servicios para Concesionarios',
    desc_en: 'Running a dealership? We handle your entire signing package — professionally, accurately, and on your timeline.',
    desc_es: 'Servicios notariales para concesionarios',
    services_en: ['Vehicle Purchase Contract','Title Transfer','Lease Agreement (Auto)','Finance Documents Package','Power of Attorney (Vehicle)','Odometer Disclosure Statement','Bill of Sale (Vehicle)','Dealer Reassignment Form'],
    services_es: ['Contrato de Compra de Vehículo','Transferencia de Título','Contrato de Arrendamiento (Auto)','Paquete de Documentos de Financiamiento','Poder Notarial (Vehículo)','Declaración de Odómetro','Factura de Venta (Vehículo)','Formulario de Reasignación'],
    plains_en: ['Notarizing the sale agreement between buyer and dealer','Official transfer of vehicle ownership','Notarizing a vehicle lease contract','Full package of loan and finance docs','Authorizing someone to handle vehicle transactions','Federal form disclosing mileage at time of sale','Proof of vehicle purchase for registration','Used when a dealer sells a vehicle from another dealer'],
    plains_es: ['Notarización del acuerdo de venta','Transferencia oficial de propiedad del vehículo','Notarización de contrato de arrendamiento','Paquete de documentos de financiamiento','Autorizar transacciones de vehículo','Formulario de kilometraje al momento de la venta','Prueba de compra de vehículo','Para vehículos de otro concesionario'],
  },
  {
    id: 'international', icon: '🌍',
    name_en: 'International & Apostille',
    name_es: 'Internacional y Apostilla',
    desc_en: 'Need your document recognized in another country? We handle apostilles and international certifications so you\'re covered worldwide.',
    desc_es: 'Documentos para uso en otros países',
    services_en: ['Apostille','International Document Statement','Certified Copies'],
    services_es: ['Apostilla','Declaración de Documento Internacional','Copias Certificadas'],
    plains_en: ['Special certification to make your document valid in another country','A sworn statement for use in a foreign country','Official copies of original documents certified as true'],
    plains_es: ['Certificación para hacer válido tu documento en otro país','Una declaración jurada para uso en otro país','Copias oficiales de documentos originales certificadas'],
  },
  {
    id: 'signature', icon: '✍️',
    name_en: 'Additional Witness Services',
    name_es: 'Servicios de Testigo Adicional',
    desc_en: 'Some documents require an additional witness beyond the notary. We provide that too — so you never have to scramble to find someone last minute.',
    desc_es: 'Tener tu firma oficialmente atestiguada',
    services_en: ['Signature Witnessing','Prenuptial / Postnuptial Agreement'],
    services_es: ['Testimonio de Firma','Acuerdo Prenupcial / Postnupcial'],
    plains_en: ['A notary watches you sign and verifies your identity','Legal agreement between spouses about finances'],
    plains_es: ['Un notario observa tu firma y verifica tu identidad','Acuerdo legal entre cónyuges sobre finanzas'],
  },
  {
    id: 'wedding', icon: '💍',
    name_en: 'Weddings & Ceremonies',
    name_es: 'Bodas y Ceremonias',
    desc_en: 'Say \'I do\' with a certified notary officiating your special day. Available in Florida, Maine, Montana, Nevada, South Carolina, and Tennessee.',
    desc_es: 'Ceremonias de matrimonio oficiadas por un notario',
    services_en: ['Wedding Ceremony Officiation'],
    services_es: ['Oficialización de Ceremonia de Boda'],
    plains_en: ['A notary officiates your wedding ceremony (Florida, Maine, Montana, Nevada, South Carolina, and Tennessee only)'],
    plains_es: ['Un notario oficia tu ceremonia de boda (Florida, Maine, Montana, Nevada, Carolina del Sur y Tennessee)'],
  },
]

// ── ALL HARDCODED SERVICE NAMES (normalized) for dedup check ──
const HARDCODED_SERVICES = new Set(
  SERVICE_CATEGORIES.flatMap(c => c.services_en).map(s => s.toLowerCase().trim())
)

// ── ALL HARDCODED CATEGORY NAMES (normalized) for dedup check ──
const HARDCODED_CATEGORY_NAMES = new Set(
  SERVICE_CATEGORIES.map(c => c.name_en.toLowerCase().trim())
)

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }

  .sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
  :focus-visible { outline:2px solid #4D4E8D; outline-offset:3px; border-radius:6px; }

  .svc-page { min-height:100vh; background:#E9EAEC; color:#151715; font-family:'Plus Jakarta Sans',-apple-system,BlinkMacSystemFont,system-ui,sans-serif; -webkit-font-smoothing:antialiased; }

  /* HERO */
  .svc-hero { padding:54px 44px 40px; text-align:center; max-width:840px; margin:0 auto; }
  .svc-hero-eyebrow { font-size:12px; font-weight:700; letter-spacing:.18em; text-transform:uppercase; color:#8A8F94; margin-bottom:16px; }
  .svc-hero-rule { width:44px; height:3px; border-radius:2px; background:#4D4E8D; margin:0 auto 22px; }
  .svc-hero h1 { font-size:clamp(30px,3.6vw,44px); font-weight:800; color:#151715; margin-bottom:16px; line-height:1.14; letter-spacing:-.025em; }
  .svc-hero h1 span { color:#4D4E8D; }
  .svc-hero-sub { font-size:16px; font-weight:500; color:#5C6065; max-width:540px; margin:0 auto 34px; line-height:1.65; }

  /* PATHWAYS */
  .svc-paths { display:grid; grid-template-columns:1fr 1fr; gap:16px; max-width:580px; margin:0 auto 18px; }
  @media(max-width:580px){ .svc-paths { grid-template-columns:1fr; } }
  .svc-path-card { background:transparent; border:none; border-radius:0; padding:12px 16px; text-align:center; box-shadow:none; }
  .svc-path-card:hover { transform:none; box-shadow:none; }
  .svc-path-h3 { font-size:13px; color:#5C6065; margin-bottom:14px; font-weight:600; }
  .svc-path-btn { display:inline-block; padding:12px 24px; border-radius:11px; font-size:14px; font-weight:700; cursor:pointer; border:none; font-family:inherit; transition:all .2s; text-decoration:none; }
  .svc-path-btn.guest { background:#4D4E8D; color:#fff; box-shadow:0 8px 18px rgba(77,78,141,0.22); }
  .svc-path-btn.guest:hover { background:#41427a; transform:translateY(-2px); }
  .svc-path-btn.signin { background:#fff; color:#4D4E8D; border:1.5px solid #CFD3D8; }
  .svc-path-btn.signin:hover { border-color:#4D4E8D; transform:translateY(-2px); }

  /* MAIN */
  .svc-main { max-width:860px; margin:0 auto; padding:32px 24px 140px; }
  .svc-instruction { font-size:14px; font-weight:500; color:#5C6065; margin-bottom:26px; text-align:center; line-height:1.7; }

  /* CATEGORIES */
  .svc-cat { background:#fff; border:1px solid #E2E4E7; border-radius:16px; margin-bottom:12px; overflow:hidden; box-shadow:0 6px 18px rgba(21,23,21,0.04); transition:border-color .2s, box-shadow .2s; }
  .svc-cat:hover { border-color:#C7CBE2; }
  .svc-cat.open { border-color:#4D4E8D; box-shadow:0 12px 28px rgba(77,78,141,0.10); }
  .svc-cat-btn { display:flex; align-items:center; gap:14px; padding:20px 22px; background:transparent; border:none; width:100%; cursor:pointer; text-align:left; transition:background .2s; font-family:inherit; }
  .svc-cat-btn:hover { background:#F5F6F7; }
  .svc-cat.open .svc-cat-btn { background:#EEF0F8; }
  .svc-cat-icon { font-size:26px; flex-shrink:0; }
  .svc-cat-info { flex:1; min-width:0; }
  .svc-cat-name { font-size:16px; font-weight:700; color:#151715; margin-bottom:2px; display:block; letter-spacing:-.01em; }
  .svc-cat-desc { font-size:12.5px; font-weight:500; color:#5C6065; display:block; }
  .svc-cat-count { font-size:11px; font-weight:700; color:#4D4E8D; background:#EEF0F8; padding:5px 12px; border-radius:20px; flex-shrink:0; white-space:nowrap; }
  .svc-cat-arrow { font-size:14px; color:#9AA0A6; flex-shrink:0; transition:transform .25s; }
  .svc-cat.open .svc-cat-arrow { transform:rotate(180deg); color:#4D4E8D; }
  @media(max-width:480px){
    .svc-cat-btn { padding:14px 16px; gap:10px; }
    .svc-cat-icon { font-size:20px; }
    .svc-cat-name { font-size:14px; }
    .svc-cat-desc { font-size:11px; white-space:normal; word-break:break-word; }
    .svc-cat-count { font-size:10px; padding:3px 8px; }
    .svc-cat-info { overflow:hidden; }
  }

  /* SERVICES LIST */
  .svc-list { display:none; padding:6px 20px 18px; }
  .svc-cat.open .svc-list { display:block; }
  .svc-item { display:flex; align-items:center; justify-content:space-between; gap:14px; padding:14px 16px; margin-bottom:8px; border-radius:12px; background:#F7F8F9; border:1px solid #E8EAEC; transition:all .2s; flex-wrap:wrap; }
  .svc-item:hover { background:#EEF0F8; border-color:#C7CBE2; }
  .svc-item.added { background:#EAF2EE; border-color:#A9CFBD; }
  .svc-item-body { flex:1; min-width:0; }
  .svc-item-name { font-size:14px; font-weight:600; color:#151715; margin-bottom:3px; display:block; }
  .svc-item-plain { font-size:11.5px; font-weight:500; color:#7C8085; line-height:1.5; display:block; }
  .svc-add-btn { flex-shrink:0; padding:8px 16px; border-radius:20px; font-size:11px; font-weight:700; cursor:pointer; border:1.5px solid; font-family:inherit; letter-spacing:.04em; transition:all .2s; white-space:nowrap; }
  .svc-add-btn.default { background:transparent; color:#4D4E8D; border-color:#BDC0DA; }
  .svc-add-btn.default:hover { background:#EEF0F8; border-color:#4D4E8D; }
  .svc-add-btn.added { background:#5E8A72; color:#fff; border-color:#5E8A72; }
  .svc-add-btn.added:hover { background:#4F7561; }
  @media(max-width:480px){
    .svc-item { flex-direction:column; align-items:flex-start; gap:10px; }
    .svc-add-btn { width:100%; text-align:center; }
  }

  /* STICKY CART BAR */
  .svc-cart-bar { position:fixed; bottom:0; left:0; right:0; z-index:200; transform:translateY(100%); transition:transform .35s cubic-bezier(0.175,0.885,0.32,1.275); }
  .svc-cart-bar.visible { transform:translateY(0); }
  .svc-cart-bar-inner { background:#4D4E8D; padding:15px 24px; display:flex; align-items:center; justify-content:space-between; gap:16px; box-shadow:0 -8px 32px rgba(21,23,21,0.18); }
  .svc-cart-info { display:flex; align-items:center; gap:12px; }
  .svc-cart-count { background:#fff; color:#4D4E8D; font-size:13px; font-weight:800; width:28px; height:28px; border-radius:50%; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .svc-cart-text { font-size:14px; color:rgba(255,255,255,0.85); }
  .svc-cart-text strong { color:#fff; }
  .svc-cart-review-btn { background:#fff; color:#4D4E8D; border:none; padding:12px 26px; border-radius:11px; font-size:14px; font-weight:700; cursor:pointer; font-family:inherit; transition:transform .2s; white-space:nowrap; flex-shrink:0; }
  .svc-cart-review-btn:hover { transform:translateY(-2px); }
  @media(max-width:480px){ .svc-cart-bar-inner { padding:12px 16px; } .svc-cart-review-btn { padding:10px 18px; font-size:13px; } }

  /* BACK TO TOP */
  .svc-backtop { position:fixed; bottom:80px; right:20px; width:46px; height:46px; border-radius:50%; background:#4D4E8D; color:#fff; border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:18px; box-shadow:0 8px 20px rgba(77,78,141,0.35); transition:all .3s; opacity:0; pointer-events:none; z-index:150; }
  .svc-backtop.show { opacity:1; pointer-events:auto; }
  .svc-backtop.cart-visible { bottom:90px; }
  .svc-backtop:hover { transform:translateY(-3px); }
`

export default function ServicesPage() {
  const { t, language }   = useLanguage()
  const navigate          = useNavigate()
  const isEs              = language === 'es'
  const [scrolled,   setScrolled]   = useState(false)
  const [showTop,    setShowTop]    = useState(false)
  const [openCats,   setOpenCats]   = useState({})
  const [cart,       setCart]       = useState([])
  const [categories, setCategories] = useState(SERVICE_CATEGORIES)
  const [dbServices, setDbServices] = useState([])

  // ── SCROLL ──────────────────────────────────────────────────
  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 60)
      setShowTop(window.scrollY > 400)
    }
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // ── SYNC WITH ADMIN DB ──────────────────────────────────────
  // ✅ FIX: Filter out DB categories that duplicate hardcoded ones
  // This prevents the "Sesame Street" effect where individual services
  // from the DB render as orphan single-item accordion cards
  useEffect(() => {
    supabase.from('service_categories').select('*').order('name_en')
      .then(({ data, error }) => {
        if (!error && data && data.length > 0) {
          const dbCats = data
            .map(c => ({
              id: `db_${c.id}`, icon: c.icon || '📋',
              name_en: c.name_en, name_es: c.name_es || c.name_en,
              desc_en: c.desc_en || '', desc_es: c.desc_es || c.desc_en || '',
              services_en: c.services_en || [], services_es: c.services_es || c.services_en || [],
              plains_en: c.plains_en || [], plains_es: c.plains_es || c.plains_en || [],
              isFromDB: true,
            }))
            // ✅ Filter 1: Remove DB cats whose NAME matches a hardcoded category
            .filter(c => !HARDCODED_CATEGORY_NAMES.has(c.name_en.toLowerCase().trim()))
            // ✅ Filter 2: Remove DB cats where ALL services already exist in hardcoded cats
            .filter(c => {
              const dbSvcs = (c.services_en || []).map(s => s.toLowerCase().trim())
              if (dbSvcs.length === 0) return false
              const allExist = dbSvcs.every(s => HARDCODED_SERVICES.has(s))
              return !allExist
            })
            // ✅ Filter 3: Remove individual service names masquerading as categories
            .filter(c => (c.services_en || []).length > 1)

          const nonEmpty = dbCats.filter(c => (c.services_en || []).length > 0)
          setCategories([...SERVICE_CATEGORIES, ...nonEmpty])
        }
      })
    supabase.from('services').select('*').order('category').order('name')
      .then(({ data, error }) => { if (!error && data) setDbServices(data) })
  }, [])

  // ── CART ACTIONS ────────────────────────────────────────────
  function addToCart(catId, svcName) {
    const id = `${catId}::${svcName}`
    setCart(prev => prev.find(i => i.id === id) ? prev : [...prev, { id, name: svcName, categoryId: catId }])
  }
  function removeFromCart(id) {
    setCart(prev => prev.filter(i => i.id !== id))
  }
  function isInCart(catId, svcName) {
    return cart.some(i => i.id === `${catId}::${svcName}`)
  }
  function toggleCat(id) {
    setOpenCats(prev => ({ ...prev, [id]: !prev[id] }))
  }

  // ── REVIEW QUOTE ────────────────────────────────────────────
  function reviewQuote() {
    sessionStorage.setItem('lca_quote_cart', JSON.stringify(cart))
    navigate('/book?mode=quote')
  }

  return (
    <div className="svc-page">
      <style>{STYLES}</style>


      <SiteHeader />

      {/* HERO */}
      <header role="banner">
        <section className="svc-hero" aria-labelledby="hero-heading">
          <p className="svc-hero-eyebrow" aria-hidden="true">
            {isEs ? 'CONSTRUYE TU COTIZACIÓN' : 'Need a notary? We come to you — anywhere in the U.S.'}
          </p>
          <div className="svc-hero-rule" aria-hidden="true" />
          <h1 id="hero-heading">
            {isEs
              ? <>Servicios Notariales Profesionales:<br /><span>Construye tu Cotización en Segundos</span></>
              : <>Same-Day, Stress-Free<br /><span>Appointments Available.</span></>
            }
          </h1>
          <p className="svc-hero-sub">
            {isEs
              ? 'Ejecute sus documentos de forma segura en cualquier lugar de los EE. UU. Todas las cotizaciones son todo incluido, sin tarifas ocultas.'
              : 'Simply select the service or services for your instant, all-inclusive quote with no hidden fees. What you see is what you pay — and that\'s it!'}
          </p>

          {/* PATHWAYS */}
          <div className="svc-paths" role="group" aria-label={isEs ? 'Opciones de inicio' : 'Getting started options'}>
            <div className="svc-path-card">
              <h3 className="svc-path-h3">{isEs ? '¿Cliente Nuevo?' : 'New Customer?'}</h3>
              <button
                className="svc-path-btn guest"
                onClick={() => document.getElementById('service-categories')?.scrollIntoView({ behavior:'smooth' })}
                aria-label={isEs ? 'Comenzar a construir una cotización como invitado' : 'Start building a notary quote as a guest'}>
                {isEs ? 'Iniciar Cotización →' : 'Start Guest Quote →'}
              </button>
            </div>
            <div className="svc-path-card">
              <h3 className="svc-path-h3">{isEs ? '¿Cliente Frecuente?' : 'Returning Customer?'}</h3>
              <Link
                to="/login"
                className="svc-path-btn signin"
                aria-label={isEs ? 'Iniciar sesión para cargar información guardada' : 'Sign in to your account to load saved information'}>
                {isEs ? 'Iniciar Sesión →' : 'Sign In to My Account →'}
              </Link>
            </div>
          </div>

          <div style={{ marginTop:18, fontSize:12, fontWeight:500, color:'#7C8085' }}>
            🎖️{' '}
            <img src="/veteran-badge.png" alt="Official seal certifying Notary Signing Vault, LLC as a Veteran and Hispanic Owned business." style={{ display:'none' }} />
            {isEs ? 'Empresa certificada de propiedad veterana e hispana' : 'Certified Veteran & Hispanic Owned Business'}
          </div>
        </section>
      </header>

      {/* MAIN CONTENT */}
      <main id="nsv-main" tabIndex={-1}>
        <div className="svc-main">

          <p className="svc-instruction" id="categories-instruction">
            {isEs
              ? 'Selecciona una categoría para ver los servicios disponibles y agregarlos a tu cotización.'
              : 'Select a category below to view available services and add them to your quote.'}
          </p>

          <section id="service-categories" aria-labelledby="categories-heading" aria-describedby="categories-instruction">
            <h2 id="categories-heading" className="sr-only">
              {isEs ? 'Categorías de Servicios Notariales' : 'Notary Service Categories'}
            </h2>

            {categories.map(cat => {
              const isOpen  = !!openCats[cat.id]
              const name    = isEs ? cat.name_es   : cat.name_en
              const desc    = isEs ? cat.desc_es   : cat.desc_en
              const svcs    = isEs ? cat.services_es : cat.services_en
              const plains  = isEs ? cat.plains_es  : cat.plains_en
              const enSvcs  = cat.services_en || []

              if (!svcs || svcs.length === 0) return null

              const addedInCat = enSvcs.filter(s => isInCart(cat.id, s)).length

              return (
                <div key={cat.id} className={`svc-cat${isOpen ? ' open' : ''}`} role="region" aria-label={name}>
                  <button
                    className="svc-cat-btn"
                    onClick={() => toggleCat(cat.id)}
                    aria-expanded={isOpen}
                    aria-controls={`cat-list-${cat.id}`}
                    type="button">
                    <span className="svc-cat-icon" aria-hidden="true">{cat.icon}</span>
                    <span className="svc-cat-info">
                      <span className="svc-cat-name">{name}</span>
                      <span className="svc-cat-desc">{desc}</span>
                    </span>
                    <span className="svc-cat-count" aria-hidden="true">
                      {addedInCat > 0 ? `✓ ${addedInCat} added` : `${svcs.length} ${isEs ? 'servicios' : 'services'}`}
                    </span>
                    <span className="svc-cat-arrow" aria-hidden="true">▾</span>
                  </button>

                  <ul id={`cat-list-${cat.id}`} className="svc-list" role="list" aria-label={`${name} services`}>
                    {svcs.map((svc, j) => {
                      const enName = enSvcs[j] || svc
                      const plain  = (plains || [])[j]
                      const added  = isInCart(cat.id, enName)
                      return (
                        <li key={j} className={`svc-item${added ? ' added' : ''}`} role="listitem">
                          <span className="svc-item-body">
                            <span className="svc-item-name">{svc}</span>
                            {plain && <span className="svc-item-plain">{plain}</span>}
                          </span>
                          <button
                            className={`svc-add-btn${added ? ' added' : ' default'}`}
                            onClick={() => added ? removeFromCart(`${cat.id}::${enName}`) : addToCart(cat.id, enName)}
                            aria-label={added
                              ? `${isEs ? 'Quitar' : 'Remove'} ${svc} ${isEs ? 'de la cotización' : 'from quote'}`
                              : `${isEs ? 'Agregar' : 'Add'} ${svc} ${isEs ? 'a la cotización' : 'to quote'}`}
                            type="button">
                            {added ? (isEs ? '✓ Agregado' : '✓ Added') : (isEs ? '+ Agregar' : '+ Add to Quote')}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )
            })}
          </section>

          {/* TRUST BADGES */}
          <div style={{ marginTop:40, textAlign:'center', padding:'30px 26px', background:'#fff', borderRadius:18, border:'1px solid #E2E4E7', boxShadow:'0 10px 28px rgba(21,23,21,0.06)' }}>
            <div style={{ fontSize:18, fontWeight:700, color:'#4D4E8D', marginBottom:14, letterSpacing:'-0.01em' }}>Why Choose Notary Signing Vault?</div>
            <div style={{ display:'flex', justifyContent:'center', gap:32, flexWrap:'wrap' }}>
              {[
                { icon:'✅', text: isEs ? 'Precios todo incluido' : 'All-inclusive pricing' },
                { icon:'🎖️', text: isEs ? 'Empresa veterana e hispana' : 'Veteran & Hispanic owned' },
                { icon:'🌎', text: isEs ? 'Cobertura nacional' : 'Nationwide coverage' },
                { icon:'⚡', text: isEs ? 'Respuesta rápida' : 'Fast response' },
              ].map((b, i) => (
                <div key={i} style={{ fontSize:13, fontWeight:500, color:'#5C6065', display:'flex', alignItems:'center', gap:6 }}>
                  <span aria-hidden="true">{b.icon}</span>{b.text}
                </div>
              ))}
            </div>
          </div>

        </div>
      </main>

      <SiteFooter />

      {/* STICKY CART BAR */}
      <div
        className={`svc-cart-bar${cart.length > 0 ? ' visible' : ''}`}
        role="region"
        aria-label={isEs ? 'Resumen de cotización' : 'Quote cart summary'}
        aria-live="polite">
        <div className="svc-cart-bar-inner">
          <div className="svc-cart-info">
            <div className="svc-cart-count" aria-label={`${cart.length} ${isEs ? 'servicios seleccionados' : 'services selected'}`}>
              {cart.length}
            </div>
            <span className="svc-cart-text">
              <strong>{cart.length} {isEs ? (cart.length === 1 ? 'servicio' : 'servicios') : (cart.length === 1 ? 'service' : 'services')}</strong>
              {' '}{isEs ? 'agregado(s) a tu cotización' : 'added to your quote'}
            </span>
          </div>
          <button
            className="svc-cart-review-btn"
            onClick={reviewQuote}
            aria-label={`${isEs ? 'Revisar cotización con' : 'Review quote with'} ${cart.length} ${isEs ? 'servicios' : 'services'}`}>
            {isEs ? 'Revisar Cotización →' : 'Review My Quote →'}
          </button>
        </div>
      </div>

      {/* BACK TO TOP */}
      <button
        className={`svc-backtop${showTop ? ' show' : ''}${cart.length > 0 ? ' cart-visible' : ''}`}
        onClick={() => window.scrollTo({ top:0, behavior:'smooth' })}
        aria-label={isEs ? 'Volver arriba' : 'Back to top'}>
        ↑
      </button>
    </div>
  )
}
